// src/pages/FounderProfile.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getEmployeeProfile } from '@/lib/employeeStorage';
import {
  ArrowLeft,
  Mail,
  MapPin,
  Calendar,
  Star,
  Linkedin,
  Twitter,
  Save,
  Loader2,
  Edit2,
  Building2,
  Lightbulb,
  Globe,
  Target,
  Eye,
} from 'lucide-react';

interface FounderData {
  id: string;
  name: string;
  email: string;
  company_name: string;
  about?: string;        // Company description / about section
  vision?: string;       // Vision & mission section
  location?: string;
  linkedin_url?: string;
  twitter_url?: string;
  website?: string;
  avatar_url?: string;
  joined_at: string;
}

export default function FounderProfile() {
  const { user, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [founder, setFounder] = useState<FounderData | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    about: '',
    vision: '',
    location: '',
    linkedin_url: '',
    twitter_url: '',
    website: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/dashboard');
      return;
    }
    
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('founder_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Database error:', error);
      }

      if (data) {
        setFounder(data);
        setFormData({
          name: data.name || user.name,
          company_name: data.company_name || '',
          about: data.about || '',
          vision: data.vision || '',
          location: data.location || '',
          linkedin_url: data.linkedin_url || '',
          twitter_url: data.twitter_url || '',
          website: data.website || ''
        });
      } else {
        setFounder({
          id: user.id,
          name: user.name,
          email: user.email,
          company_name: '',
          joined_at: new Date().toISOString()
        });
        setFormData({
          name: user.name,
          company_name: '',
          about: '',
          vision: '',
          location: '',
          linkedin_url: '',
          twitter_url: '',
          website: ''
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      if (formData.name !== user.name) {
        await updateUserProfile({ name: formData.name });
      }

      const { error } = await supabase
        .from('founder_profiles')
        .upsert({
          user_id: user.id,
          name: formData.name,
          company_name: formData.company_name,
          about: formData.about,
          vision: formData.vision,
          location: formData.location,
          linkedin_url: formData.linkedin_url,
          twitter_url: formData.twitter_url,
          website: formData.website,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast({
        title: 'Profile Updated',
        description: 'Your profile has been saved successfully',
      });
      
      setEditing(false);
      loadProfile();
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile changes',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const profile = founder ? getEmployeeProfile(founder.email, 'founder') : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto">
      {/* Compact header with back button */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b px-4 py-2">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/dashboard')} 
          className="gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-4">
        {/* Profile Card - More compact */}
        <Card className="overflow-hidden border shadow-sm">
          {/* Header gradient - shorter */}
          <div className="h-20 bg-gradient-to-r from-green-600 to-emerald-600" />
          
          <CardContent className="relative pt-0 px-4 pb-4">
            <div className="flex flex-col md:flex-row gap-4 -mt-10">
              {/* Profile Photo - smaller */}
              <div className="flex-shrink-0">
                <div className="h-20 w-20 rounded-full overflow-hidden border-4 border-white shadow-md bg-white">
                  {profile?.imageUrl && !imageError ? (
                    <img 
                      src={profile.imageUrl}
                      alt={founder?.name}
                      className="h-full w-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-xl font-bold bg-green-600 text-white">
                      {getInitials(founder?.name || '')}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Profile Info - compact */}
              <div className="flex-1 pt-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h1 className="text-xl font-bold">{founder?.name}</h1>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap text-sm">
                      <span className="text-muted-foreground">Founder</span>
                      {founder?.company_name && (
                        <>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {founder.company_name}
                          </span>
                        </>
                      )}
                      {founder?.location && (
                        <>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {founder.location}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Edit Button */}
                  <Button 
                    size="sm"
                    variant={editing ? "outline" : "default"}
                    onClick={() => setEditing(!editing)}
                  >
                    {editing ? (
                      <>Cancel</>
                    ) : (
                      <>
                        <Edit2 className="h-3.5 w-3.5 mr-1.5" />
                        Edit Profile
                      </>
                    )}
                  </Button>
                </div>

                {/* Quick preview - only show in view mode */}
                {!editing && founder?.about && (
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                    {founder.about.length > 100 ? founder.about.substring(0, 100) + '...' : founder.about}
                  </p>
                )}

                {editing && (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Full Name</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your name"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Email</Label>
                        <Input
                          value={founder?.email || ''}
                          disabled
                          className="bg-muted h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Startup Name</Label>
                        <Input
                          value={formData.company_name}
                          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                          placeholder="Your startup name"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Location</Label>
                        <Input
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          placeholder="City, Country"
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1">
                        <Lightbulb className="h-3 w-3" />
                        About / Company Description
                      </Label>
                      <Textarea
                        value={formData.about}
                        onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                        placeholder="Describe your company, what you do, your mission, and your story..."
                        rows={3}
                        className="text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        This will appear in the "About" tab. Tell your company's story.
                      </p>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1">
                        <Target className="h-3 w-3" />
                        Vision & Mission
                      </Label>
                      <Textarea
                        value={formData.vision}
                        onChange={(e) => setFormData({ ...formData, vision: e.target.value })}
                        placeholder="What is your vision for the future? What problem are you solving? What is your mission?"
                        rows={3}
                        className="text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        This will appear in the "Vision" tab. Share your vision, mission, and the problem you're solving.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Website</Label>
                        <Input
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          placeholder="https://..."
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">LinkedIn</Label>
                        <Input
                          value={formData.linkedin_url}
                          onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                          placeholder="LinkedIn profile URL"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Twitter/X</Label>
                        <Input
                          value={formData.twitter_url}
                          onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                          placeholder="Twitter profile URL"
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSave} disabled={saving}>
                        {saving ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : (
                          <Save className="h-3.5 w-3.5 mr-1" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                )}

                {/* Contact Info - compact */}
                {!editing && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                      <div className="truncate">
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-xs font-medium truncate">{founder?.email}</p>
                      </div>
                    </div>
                    {founder?.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                        <div className="truncate">
                          <p className="text-xs text-muted-foreground">Website</p>
                          <a href={founder.website} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary hover:underline truncate block">
                            {founder.website.replace('https://', '').replace('http://', '').substring(0, 20)}
                          </a>
                        </div>
                      </div>
                    )}
                    {founder?.linkedin_url && (
                      <div className="flex items-center gap-2">
                        <Linkedin className="h-3.5 w-3.5 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">LinkedIn</p>
                          <a href={founder.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary hover:underline">
                            Profile
                          </a>
                        </div>
                      </div>
                    )}
                    {founder?.twitter_url && (
                      <div className="flex items-center gap-2">
                        <Twitter className="h-3.5 w-3.5 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Twitter</p>
                          <a href={founder.twitter_url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary hover:underline">
                            Profile
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs - compact */}
        <Tabs defaultValue="about" className="mt-4">
          <TabsList className="h-8">
            <TabsTrigger value="about" className="text-xs px-3 flex items-center gap-1">
              <Lightbulb className="h-3 w-3" />
              About
            </TabsTrigger>
            <TabsTrigger value="vision" className="text-xs px-3 flex items-center gap-1">
              <Target className="h-3 w-3" />
              Vision
            </TabsTrigger>
          </TabsList>

          {/* About Tab - Company description */}
          <TabsContent value="about" className="mt-3">
            <Card className="border shadow-sm">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" />
                  About {founder?.company_name || 'the Founder'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-3">
                <div className="prose prose-sm max-w-none">
                  {founder?.about ? (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {founder.about}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      No description added yet. Click "Edit Profile" to tell us about your company.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="mt-3 border shadow-sm">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Journey
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Member since</p>
                    <p className="text-sm font-medium">
                      {new Date(founder?.joined_at || '').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vision Tab - Vision & Mission */}
          <TabsContent value="vision" className="mt-3">
            <Card className="border shadow-sm">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Vision & Mission
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-3">
                {founder?.vision ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xs font-semibold mb-1 flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        Vision
                      </h3>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {founder.vision}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No vision statement added yet. Click "Edit Profile" to share your vision and mission.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Optional: Separate Mission section if you want to split further */}
            {founder?.vision && founder.vision.includes('Mission') && (
              <Card className="mt-3 border shadow-sm">
                <CardHeader className="pb-2 pt-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Mission
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 pb-3">
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {founder.vision.includes('Mission:') 
                      ? founder.vision.split('Mission:')[1]?.trim() 
                      : founder.vision}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}